import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const CONTACT_INFO = [
  {
    icon: <Phone size={22} />,
    title: "Phone",
    details: [
      { text: "+91 83082 03097 (Mr. Kunal Zende)", href: "tel:+918308203097" },
      { text: "+91 96802 49797 (Mr. Umesh Zende)", href: "tel:+919680249797" }
    ]
  },
  {
    icon: <Mail size={22} />,
    title: "Email",
    details: [
      { text: "shriramfoodsprocessing@gmail.com", href: "mailto:shriramfoodsprocessing@gmail.com" }
    ]
  },
  {
    icon: <MapPin size={22} />,
    title: "Address",
    details: [
      { text: "Saswad, Pune, Maharashtra 412301", href: null }
    ]
  },
  {
    icon: <Clock size={22} />,
    title: "Business Hours",
    details: [
      { text: "All Days: 9:00 AM – 6:00 PM", href: null }
    ]
  }
];

export default function Contact() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  useEffect(() => {
    // Tally embed script can be loaded when user provides their Tally form ID
    // Uncomment and update the form ID below:
    // const script = document.createElement("script");
    // script.src = "https://tally.so/widgets/embed.js";
    // script.async = true;
    // document.body.appendChild(script);
  }, []);

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Header — with parallax background */}
      <div ref={heroRef} className="relative overflow-hidden">
        <motion.img
          src="https://images.unsplash.com/photo-1760463921652-78b38572da45?w=1600&q=80"
          alt=""
          aria-hidden="true"
          style={{ y: imageY }}
          className="absolute inset-0 w-full h-[150%] object-cover opacity-[0.13] scale-110"
        />
        <section className="py-12 md:py-16 bg-[#f8f8f8]/80">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
            <Badge className="bg-green-100 text-[#2B7A1E] hover:bg-green-100 mb-4 px-4 py-1.5 text-xs font-semibold tracking-wide rounded-full border-0">
              Get in Touch
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#333] tracking-tight mb-3" style={{ fontFamily: 'Montserrat' }}>
              Contact Us
            </h1>
            <p className="text-gray-600 max-w-xl">
              We'd love to hear from you! Whether you need product information, pricing, or custom solutions, our team is always a message away.
            </p>
          </div>
        </section>
      </div>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
            {/* Left: Contact Info + Map */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="lg:col-span-2"
            >
              <motion.h2 variants={fadeUp} className="text-2xl font-bold text-[#333] mb-8" style={{ fontFamily: 'Montserrat' }}>
                Contact Information
              </motion.h2>

              <div className="space-y-6 mb-10">
                {CONTACT_INFO.map((info) => (
                  <motion.div key={info.title} variants={fadeUp} className="flex gap-4" data-testid={`contact-${info.title.toLowerCase()}`}>
                    <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center text-[#2B7A1E] flex-shrink-0">
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-[#333] mb-1" style={{ fontFamily: 'Montserrat' }}>{info.title}</h3>
                      {info.details.map((d) => (
                        d.href ? (
                          <a key={d.text} href={d.href} className="block text-sm text-gray-600 hover:text-[#2B7A1E] transition-colors">{d.text}</a>
                        ) : (
                          <p key={d.text} className="text-sm text-gray-600">{d.text}</p>
                        )
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* WhatsApp Button */}
              <motion.div variants={fadeUp}>
                <a
                  href="https://wa.me/918308203097?text=Hi%2C%20I%20came%20from%20your%20website.%20I%20want%20details%20about%20frozen%20foods%20and%20other%20products."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold h-12 shadow-lg gap-2" data-testid="contact-whatsapp-btn">
                    <MessageCircle size={20} /> Message on WhatsApp
                  </Button>
                </a>
              </motion.div>

              {/* Map */}
              <motion.div variants={fadeUp} className="mt-8 rounded-2xl overflow-hidden shadow-md border border-gray-100" data-testid="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1224.7056583730662!2d74.0172451035632!3d18.404301143426018!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2efe1cb6434fd%3A0x663ba8494d1329a7!2sShriram%20Frozen%20Foods!5e1!3m2!1sen!2sus!4v1772098173894!5m2!1sen!2sus"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Shri Ram Frozen Foods Location"
                />
              </motion.div>
            </motion.div>

            {/* Right: Tally Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-3"
            >
              <h2 className="text-2xl font-bold text-[#333] mb-2" style={{ fontFamily: 'Montserrat' }}>
                Send Us a Message
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>

              {/* Contact Form - Tally Integration Ready */}
              <div className="bg-[#f8f8f8] rounded-3xl p-6 md:p-8 border border-gray-100" data-testid="contact-form-container">
                <div className="space-y-5">
                  <div className="text-center py-6 bg-white rounded-2xl border border-dashed border-green-300">
                    <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                      <MessageCircle size={24} className="text-[#2B7A1E]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#333] mb-2" style={{ fontFamily: 'Montserrat' }}>
                      Get in Touch With Us
                    </h3>
                    <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
                      For the fastest response, reach out to us via WhatsApp or phone. We typically respond within 30 minutes during business hours.
                    </p>
                    <div className="flex flex-col gap-3 max-w-xs mx-auto">
                      <a
                        href="https://wa.me/918308203097?text=Hi%2C%20I%20came%20from%20your%20website.%20I%20want%20details%20about%20frozen%20foods%20and%20other%20products."
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button className="w-full rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold h-12 shadow-lg gap-2" data-testid="form-whatsapp-btn">
                          <MessageCircle size={18} /> WhatsApp Us
                        </Button>
                      </a>
                      <a href="tel:+918308203097">
                        <Button variant="outline" className="w-full rounded-full border-2 border-[#2B7A1E] text-[#2B7A1E] font-semibold h-12 hover:bg-green-50 gap-2" data-testid="form-call-btn">
                          <Phone size={18} /> Call +91 83082 03097
                        </Button>
                      </a>
                      <a href="mailto:shriramfoodsprocessing@gmail.com">
                        <Button variant="outline" className="w-full rounded-full border border-gray-200 text-gray-600 font-medium h-12 hover:bg-gray-50 gap-2" data-testid="form-email-btn">
                          <Mail size={18} /> Send an Email
                        </Button>
                      </a>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-5 border border-gray-100">
                    <h4 className="text-sm font-bold text-[#333] mb-3" style={{ fontFamily: 'Montserrat' }}>What to include in your inquiry:</h4>
                    <ul className="space-y-2">
                      {[
                        "Product name and quantity required",
                        "Preferred packaging size",
                        "Delivery location and timeline",
                        "Company name and contact details",
                        "Any special requirements"
                      ].map((item) => (
                        <li key={item} className="flex gap-2 text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#2B7A1E] mt-2 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#2B7A1E]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Montserrat' }}>
            Need Immediate Assistance?
          </h2>
          <p className="text-white/80 mb-6">
            Call us directly or send a WhatsApp message for quick responses.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="tel:+918308203097">
              <Button className="rounded-full bg-white text-[#2B7A1E] font-bold px-8 h-11 hover:bg-gray-100 shadow-lg gap-2" data-testid="contact-cta-call">
                <Phone size={18} /> Call Now
              </Button>
            </a>
            <a href="https://wa.me/918308203097?text=Hi%2C%20I%20came%20from%20your%20website.%20I%20want%20details%20about%20frozen%20foods%20and%20other%20products." target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full border-2 border-white text-white font-semibold px-8 h-11 hover:bg-white/10 gap-2" data-testid="contact-cta-whatsapp">
                <MessageCircle size={18} /> WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
